import React, { useState, useCallback } from "react";
import { FieldLabel } from "@measured/puck";
import { MacroMarkdownEditorModal } from "./MacroMarkdownEditorModal";

export interface MacroMarkdownEditorFieldProps {
  name: string;
  field: {
    label?: string;
  };
  value: string | undefined;
  onChange: (value: string) => void;
}

/**
 * Custom Puck field for editing Markdown with a modal editor.
 * Shows a button that opens a modal with a textarea for Markdown editing.
 */
export function MacroMarkdownEditorField({
  name,
  field,
  value,
  onChange,
}: MacroMarkdownEditorFieldProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleApply = useCallback(
    (newValue: string) => {
      onChange(newValue);
      setIsModalOpen(false);
    },
    [onChange]
  );

  const charCount = value?.length || 0;
  const hasContent = charCount > 0;

  // Get a preview snippet of the content
  const previewText = value
    ? value
        .substring(0, 50)
        .replace(/\n/g, " ")
        .trim() + (value.length > 50 ? "..." : "")
    : "";

  return (
    <FieldLabel label={field.label || "Markdown Content"}>
      <div className="macro-markdown-editor-field">
        <button
          type="button"
          onClick={handleOpenModal}
          className="macro-markdown-editor-field-button"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            padding: "8px 12px",
            fontSize: "13px",
            fontFamily: "inherit",
            color: hasContent ? "#065f46" : "#64748b",
            backgroundColor: hasContent ? "#ecfdf5" : "#f8fafc",
            border: `1px solid ${hasContent ? "#a7f3d0" : "#e2e8f0"}`,
            borderRadius: "6px",
            cursor: "pointer",
            transition: "all 0.15s ease",
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span style={{ fontSize: "14px" }}>{hasContent ? "M" : "+"}</span>
            <span
              style={{
                maxWidth: "180px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {hasContent ? previewText : "Add Markdown Content"}
            </span>
          </span>
          {hasContent && (
            <span
              style={{
                fontSize: "11px",
                color: "#64748b",
                backgroundColor: "#e2e8f0",
                padding: "2px 6px",
                borderRadius: "4px",
              }}
            >
              {charCount} chars
            </span>
          )}
        </button>

        <MacroMarkdownEditorModal
          isOpen={isModalOpen}
          value={value || ""}
          onClose={handleCloseModal}
          onApply={handleApply}
        />
      </div>
    </FieldLabel>
  );
}

export default MacroMarkdownEditorField;
